/**
 * Unlock via your Mac (docs/e2e.md "Unlock via a machine"), additive. A locked, trusted browser
 * sends a request signed by its device key with a fresh passkey assertion over the request digest
 * (`@viko/crypto` unlock-request.ts); the hub relays it to the account's online key-holding
 * daemons; one answers with wrapped keys. The hub stores ids, public keys, signatures, wrapped keys,
 * machine/device labels it already holds, bounded enums and timestamps: never a plaintext key.
 */
import { z } from 'zod';
import { ContentBase64Url } from './content.js';
import { ContentKeyWrap, ContentPublicKey } from './content-directory.js';
import { ApprovalBatch, WebAuthnAssertion } from './signing.js';
const Id = z.string().min(1).max(256);
const Count = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER);
const Counter = z.number().int().positive().max(Number.MAX_SAFE_INTEGER);
const Time = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER);
const Digest = ContentBase64Url.refine((s) => s.length === 43);
const Label = z.string().min(1).max(200);
/** `@viko/crypto` UnlockRefusal: why a daemon (or the hub) refused. Fixed enum, never text. */
export const UNLOCK_REFUSAL_REASONS = [
    'malformed',
    'wrong-account',
    'expired',
    'lifetime',
    'replayed',
    'unknown-device',
    'revoked-device',
    'expired-device',
    'session-device',
    'bad-signature',
    'bad-assertion',
    'untrusted-credential',
    'revoked-credential',
    'recovery-hold',
    'quarantined',
    'key-mismatch',
    'stale-key',
    'directory-fork',
    'no-key',
];
export const UnlockRefusalReason = z.enum(UNLOCK_REFUSAL_REASONS);
/** `@viko/crypto` SignedUnlockRequest. */
export const SignedUnlockRequestSchema = z.strictObject({
    statement: z.strictObject({
        v: z.literal(1),
        type: z.literal('viko.unlock-request'),
        accountId: Id,
        deviceKeyId: Id,
        agreementKey: ContentPublicKey,
        directory: z.strictObject({ revision: Count, digest: Digest.nullable(), epoch: Count }),
        nonce: Digest,
        issuedAt: Time,
        expiresAt: Time,
    }),
    signature: ContentBase64Url.length(86),
    assertion: WebAuthnAssertion.strict(),
    /** Sign-in only: the `{login, device-endorsement, unlock}` batch the assertion covers. */
    batch: ApprovalBatch.optional(),
});
/** `@viko/crypto` UnlockGrant: wrapped epoch keys only. */
export const UnlockGrantSchema = z.strictObject({
    v: z.literal(1),
    requestDigest: Digest,
    deviceKeyId: Id,
    directory: z.strictObject({ revision: Counter, digest: Digest, epoch: Counter }),
    wraps: z
        .array(z.strictObject({ epoch: Counter, wrap: ContentKeyWrap }))
        .min(1)
        .max(64),
});
export const UnlockMachine = z.strictObject({ machineId: Id, name: Label });
/** `POST /api/content/keys/unlock/machines`: online daemons that are key-directory members. */
export const UnlockMachines = z.strictObject({ machines: z.array(UnlockMachine).max(32) });
/**
 * `POST /api/content/keys/unlock/holders` (additive): every key-directory member daemon, online
 * or not, so a locked browser can say "Areks MacBook Pro · online" or "offline · last seen 11 h
 * ago" instead of a bare "no machine is online". `lastSeenAt` is the hub's last sign of life
 * (connect, keepalive or disconnect time).
 */
export const UnlockHolder = z.strictObject({
    machineId: Id,
    name: Label,
    online: z.boolean(),
    lastSeenAt: Time.optional(),
});
export const UnlockHolders = z.strictObject({ holders: z.array(UnlockHolder).max(32) });
/** `unlock/request` → the relay's handle (one pending request per device; a new one supersedes). */
export const UnlockStarted = z.strictObject({
    requestId: Id,
    expiresAt: Time,
    machines: z.array(UnlockMachine).max(32),
});
export const UnlockRef = z.strictObject({ requestId: Id });
export const UnlockStatusEnum = z.enum(['pending', 'granted', 'refused', 'expired', 'superseded']);
export const UnlockStatus = z.strictObject({
    requestId: Id,
    status: UnlockStatusEnum,
    expiresAt: Time,
    machineId: Id.optional(),
    machineName: Label.optional(),
    reason: UnlockRefusalReason.optional(),
    grant: UnlockGrantSchema.optional(),
});
/** Audit rows for You › Security and the Inbox notice on other devices. */
export const UnlockRecord = z.strictObject({
    requestId: Id,
    deviceKeyId: Id,
    deviceLabel: Label.optional(),
    status: UnlockStatusEnum,
    machineId: Id.optional(),
    machineName: Label.optional(),
    reason: UnlockRefusalReason.optional(),
    createdAt: Time,
    decidedAt: Time.optional(),
});
export const UnlockList = z.strictObject({ unlocks: z.array(UnlockRecord).max(32) });
/**
 * `unlock/credential` (additive, pin bootstrap): one of the account's passkeys by id, for a
 * browser with no local pins that has just made a fresh assertion with it. ADVISORY: the browser
 * pins `cose` only after it verifies that assertion with it (docs/security.md "Pin bootstrap from a
 * locally verified assertion"); `revoked`/`held` can only make the browser refuse.
 */
export const UnlockCredentialRef = z.strictObject({ credentialId: Id });
export const UnlockCredential = z.strictObject({
    credentialId: Id,
    /** base64url COSE public key, as stored at registration. */
    cose: ContentBase64Url.refine((s) => s.length <= 4096),
    /** The hub's last-seen signCount (0 for synced passkeys). */
    counter: Count,
    /** On the latest passkey-signed revocation list. */
    revoked: z.boolean(),
    /** Created in a recovery session that is still on its hold. */
    held: z.boolean(),
    createdAt: Time,
});
/** hub → daemon push. `heldCredentials`: passkeys under a recovery hold (only adds refusals). */
export const UnlockRequestFrame = z.strictObject({
    type: z.literal('content.unlock.request'),
    requestId: Id,
    request: SignedUnlockRequestSchema,
    heldCredentials: z.array(Id).max(256),
});
/** daemon → hub RPC params (`content.unlock.fulfil` / `content.unlock.refuse`). */
export const UnlockFulfil = z.strictObject({ requestId: Id, grant: UnlockGrantSchema });
export const UnlockRefuse = z.strictObject({
    requestId: Id,
    requestDigest: Digest,
    reason: UnlockRefusalReason,
});
//# sourceMappingURL=content-unlock.js.map